

import { useState, useEffect } from "react";
import { useAppContext } from "../../context/AppContext";
import AppFooter from "./AppFooter";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Stethoscope,
  SprayCan as Spray,
  GraduationCap,
  Dumbbell,
  Shield,
  Trophy,
  ChevronLeft,
  Send,
  Layers,
  Zap,
  Sparkles,
  BadgeCheck,
} from "lucide-react";
import ServicesByCategory from "../ServicesByCategory";
import Hero from "./Hero";

// --- DATA CONSTANTS ---

const serviceCategories = [
  { 
    label: "Doctor", 
    icon: <Stethoscope size={32} />, 
    image: "https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&q=80" 
  },
 { 
    label: "Cleaner", 
    icon: <Spray size={32} />, 
    image: "https://images.unsplash.com/photo-1581578949510-fa7315c4c350?q=80&w=687&auto=format&fit=crop&q=80" 
  },
  { 
    label: "Education", 
    icon: <GraduationCap size={32} />, 
    image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=1170&auto=format&fit=crop&q=80" 
  },
  { 
    label: "Fitness", 
    icon: <Dumbbell size={32} />, 
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80" 
  },
  { 
    label: "Security", 
    icon: <Shield size={32} />, 
    image: "https://images.unsplash.com/photo-1555963966-b7ae5404b6ed?auto=format&fit=crop&q=80" 
  },
  { 
    label: "Sports", 
    icon: <Trophy size={32} />, 
    image: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80" 
  },
];

const features = [
  {
    id: "01",
    title: "Communities",
    subtitle: "Manage your empire",
    description: "Connect with your local community and manage organization events effortlessly.",
    route: "/organizations",
    gradient: "from-primary-500/20 to-primary-900/5"
  },
  {
    id: "02",
    title: "Appointments",
    subtitle: "Track your timeline",
    description: "Never miss a beat with our intelligent appointment scheduling and tracking.",
    route: "/appointments",
    gradient: "from-secondary-500/20 to-secondary-900/5"
  },
  {
    id: "03",
    title: "Personal Events",
    subtitle: "Create moments",
    description: "Plan weddings, parties, and gatherings with comprehensive tools.",
    route: "/events",
    gradient: "from-purple-500/20 to-purple-900/5"
  },
];

const highlights = [
  {
    title: "Unified Timeline",
    description:
      "See organizations, appointments, and personal events in one fluid command center.",
    icon: <Layers size={22} />,
  },
  {
    title: "Smart Scheduling",
    description:
      "Conflicts, reminders, and auto-blocks work in the background so you stay focused.",
    icon: <Zap size={22} />,
  },
  {
    title: "Trusted Access",
    description:
      "Share only what you want. Granular visibility keeps teams aligned and secure.",
    icon: <BadgeCheck size={22} />,
  },
];

const steps = [
  {
    id: "01",
    title: "Create Spaces",
    description: "Set up your orgs, clients, or projects in under a minute.",
  },
  {
    id: "02",
    title: "Add Time Blocks",
    description: "Drag, drop, and color-code your day across every calendar.",
  },
  {
    id: "03",
    title: "Stay in Flow",
    description: "Get alerts and nudges that keep you ahead without noise.",
  },
];



const CornerBranding = ({ opacity }) => (
  <motion.div
    style={{ opacity }}
    className="fixed top-8 left-8 z-50 mix-blend-difference text-white pointer-events-none"
  >
    <h1 className="text-3xl font-black tracking-tighter leading-none">
      TIMEWISE.
    </h1>
  </motion.div>
);

const HeaderContent = ({ opacity }) => {
  const { setShowLoginModal } = useAppContext();

  return (
    <motion.div
      style={{ opacity }}
      className="fixed top-6 right-6 md:top-8 md:right-8 z-50"
    >
      <motion.button
        onClick={() => setShowLoginModal(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="hidden md:flex bg-white text-black px-8 py-3 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors shadow-lg shadow-white/10"
      >
        Get Started
      </motion.button>

      {/* Icon button for small screens */}
      <motion.button
        onClick={() => setShowLoginModal(true)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="flex md:hidden items-center justify-center bg-white text-black h-12 w-12 rounded-full shadow-md"
        aria-label="Get Started"
      >
        <Send size={24} strokeWidth={2.5} />
      </motion.button>
    </motion.div>
  );
};


// --- MAIN HOME COMPONENT ---

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();
  const { scrollY } = useScroll();
 
  const headerOpacity = useTransform(scrollY, [0, 100], [1, 0]);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedCategory]);

 

  return (
    <div className="relative bg-background min-h-screen text-white selection:bg-primary-500/30 selection:text-white overflow-x-hidden font-sans">
      <CornerBranding opacity={headerOpacity} />
      <HeaderContent opacity={headerOpacity} />

      {/* Atmosphere */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary-500/10 blur-[140px]" />
        <div className="absolute top-[35%] right-[-10%] h-[420px] w-[420px] rounded-full bg-secondary-500/15 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:48px_48px] opacity-40" />
      </div>

      {/* Background Noise Texture */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-[0]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10">
        {!selectedCategory && (
          <>
            {/* New Hero Section */}
            <Hero />

            <section className="px-6 md:px-12 lg:px-20">
              <div className="mx-auto max-w-7xl py-16 md:py-24 border-y border-white/5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.3em] text-gray-300">
                      <Sparkles size={14} className="text-primary-400" />
                      Trusted momentum
                    </div>
                    <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight font-display">
                      Built for modern schedules that move fast.
                    </h2>
                  </div>
                  <p className="text-gray-400 max-w-xl text-sm md:text-base leading-relaxed">
                    TimeWise blends productivity, clarity, and presence. The user app is
                    designed to feel effortless while still giving you serious control.
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-gray-400">
                  {["NORTHWIND", "PULSECARE", "APERTURE", "NOVA LABS", "VANTAGE"].map((brand) => (
                    <div
                      key={brand}
                      className="rounded-full border border-white/10 bg-white/5 px-4 py-2 uppercase tracking-[0.2em]"
                    >
                      {brand}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="features" className="px-6 md:px-12 lg:px-20">
              <div className="mx-auto max-w-7xl py-20 md:py-28">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12 md:mb-16 text-center"
                >
                  <p className="text-xs uppercase tracking-[0.4em] text-primary-400 mb-4">
                    Why TimeWise
                  </p>
                  <h2 className="text-3xl md:text-5xl font-bold tracking-tight font-display mb-4">
                    A calm UI with serious power.
                  </h2>
                  <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                    Designed for efficiency, built for you. Explore the pillars of our ecosystem.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      onClick={() => navigate(feature.route)}
                      className="group relative p-8 flex flex-col justify-start cursor-pointer overflow-hidden rounded-3xl min-h-[320px] bg-surface hover:bg-surface-hover border border-white/5 hover:border-white/10 transition-all duration-500"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                      <div className="relative z-10 flex justify-between items-start mb-6">
                        <span className="text-sm font-mono opacity-50 bg-black/20 px-3 py-1 rounded-full border border-white/5">
                          {feature.id}
                        </span>
                        <div className="bg-white/5 p-2 rounded-full group-hover:bg-white group-hover:text-black transition-colors duration-300">
                          <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </div>

                      <div className="relative z-10 mt-auto">
                        <h3 className="text-xl font-bold mb-2 tracking-tight group-hover:text-white text-gray-100">
                          {feature.title}
                        </h3>
                        <p className="text-xs font-medium text-primary-400 mb-4 uppercase tracking-wider opacity-80">
                          {feature.subtitle}
                        </p>
                        <p className="text-gray-400 leading-relaxed text-xs">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            <section className="px-6 md:px-12 lg:px-20">
              <div className="mx-auto max-w-7xl py-16 md:py-24 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
                <div className="space-y-8">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-secondary-300">
                      Experience
                    </p>
                    <h3 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight font-display">
                      The timeline you wish you always had.
                    </h3>
                    <p className="mt-4 text-gray-400 max-w-xl text-sm md:text-base">
                      Beautifully structured sections guide you through the day. Every interaction is
                      designed for speed, clarity, and confidence.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {highlights.map((highlight) => (
                      <div
                        key={highlight.title}
                        className="rounded-2xl border border-white/10 bg-white/5 p-5"
                      >
                        <div className="flex items-center gap-3 text-primary-300">
                          {highlight.icon}
                          <h4 className="text-sm font-semibold tracking-tight text-white">
                            {highlight.title}
                          </h4>
                        </div>
                        <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                          {highlight.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </section>

            <section className="px-6 md:px-12 lg:px-20">
              <div className="mx-auto max-w-7xl py-16 md:py-24 border-y border-white/5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 mb-10">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-primary-300">
                      How it works
                    </p>
                    <h3 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight font-display">
                      Simple steps, powerful results.
                    </h3>
                  </div>
                  <p className="text-gray-400 max-w-xl text-sm md:text-base">
                    From onboarding to daily flow, every moment is designed to help you move faster.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className="rounded-3xl border border-white/10 bg-white/5 p-6"
                    >
                      <p className="text-xs font-mono text-gray-400">STEP {step.id}</p>
                      <h4 className="mt-4 text-lg font-semibold text-white">{step.title}</h4>
                      <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        <AnimatePresence mode="wait">
          {!selectedCategory ? (
            <motion.section
              key="categories"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              exit={{ opacity: 0, y: -50 }}
              className="px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-white/5"
            >
              <div className="mx-auto max-w-7xl">
              <div className="flex flex-col md:flex-row justify-between items-end mb-16">
                <div>
                  <h3 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500 font-display">
                    SERVICES.
                  </h3>
                  <div className="h-1 w-24 bg-primary-500 rounded-full" />
                </div>
                <p className="text-gray-400 mt-8 md:mt-0 max-w-sm text-right text-sm">
                  Browse our comprehensive list of professional services tailored to your needs.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {serviceCategories.map((item, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -5 }}
                    className="group relative aspect-[4/3] overflow-hidden rounded-2xl cursor-pointer border border-white/10"
                    onClick={() => setSelectedCategory(item.label)}
                  >
                     {/* Background Image */}
                    <img 
                      src={item.image} 
                      alt={item.label} 
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black opacity-80 group-hover:opacity-60 transition-opacity duration-300" />

                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4 z-10">
                      <span className="font-bold tracking-widest text-lg uppercase text-white drop-shadow-md border-b-2 border-transparent group-hover:border-primary-500 transition-all pb-1 text-center">
                        {item.label}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
              </div>
            </motion.section>
          ) : (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="min-h-screen pt-32 pb-20 px-6 md:px-12 lg:px-20"
            >
              <div className="mx-auto max-w-7xl">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary-400 mb-8 transition-colors text-gray-400"
              >
                <ChevronLeft size={16} /> Back to Categories
              </button>

              <div className="bg-surface border border-white/5 p-4 md:p-8 rounded-3xl min-h-[60vh] backdrop-blur-sm">
                <ServicesByCategory
                  category={selectedCategory}
                  onBack={() => setSelectedCategory(null)}
                  onSelectService={(service) => {
                    const serviceId = service?.service_id || service?.id;
                    navigate(
                      serviceId ? `/app/services/${serviceId}` : "/app/services",
                      { state: { service } }
                    );
                  }}
                />
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!selectedCategory && (
          <section className="px-6 md:px-12 lg:px-20 py-20">
            <div className="mx-auto max-w-7xl rounded-3xl border border-white/10 bg-gradient-to-br from-primary-500/15 via-white/5 to-secondary-500/15 p-10 md:p-16 text-center">
              <h3 className="text-3xl md:text-5xl font-bold tracking-tight font-display">
                Make every minute feel intentional.
              </h3>
              <p className="mt-4 text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
                Start organizing your schedule with a UI that looks premium and feels effortless.
              </p>
              <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
                <button
                  onClick={() => navigate("/appointments")}
                  className="rounded-full bg-white text-black px-6 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-gray-200 transition-colors"
                >
                  Explore App
                </button>
                <button
                  onClick={() => navigate("/organizations")}
                  className="rounded-full border border-white/30 px-6 py-3 text-sm font-semibold uppercase tracking-widest hover:border-white/60 transition-colors"
                >
                  View Communities
                </button>
              </div>
            </div>
          </section>
        )}

        <AppFooter />
      </div>
    </div>
  );
};

export default Home;
